import Button from "@mui/material/Button";
import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { ApiContext } from "../../../../App";
import zod from "zod";
import PointCsvDataGrid from "./PointCsvDataGrid";
import { TRENDS_KEYS } from "../../../../util/keys";
import { convertCsvFileToJson } from "../../../../util/csvParser";
import FileInputButton from "../../../../components/FileInputButton";

const ZOD_MISSING_MESSAGE = "Field is required";
const ZOD_INVALID_TYPE_MESSAGE = "Invalid option for field";

const enumErrorFunction = (issue) =>
    issue.input === undefined ? ZOD_MISSING_MESSAGE : ZOD_INVALID_TYPE_MESSAGE;

// Re-add zod schemas (these were previously defined in this file)
const PointCreateDto = zod.object({
    facility: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    category: zod.enum(["INPUT", "OUTPUT"], {
        error: enumErrorFunction,
    }),
    identifier: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    key: zod.enum(TRENDS_KEYS, {
        error: enumErrorFunction,
    }),
    system: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    name: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    tariff: zod.string().optional(),
    controller: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    location: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    visible: zod.boolean().optional(),
    floorplan: zod.string().optional(),
    display_on_floorplan: zod.boolean(),
});

const CsvPoint = zod.object({
    category: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    identifier: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    key: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    name: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    controller: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    location: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    system: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    tariff: zod.string().optional(),
    floorplan: zod.string().optional(),
    display_on_floorplan: zod.string().optional(),
});

const csvPointArray = zod.array(CsvPoint).superRefine((points, ctx) => {
    const identifiersAndKeyAndControllerMap = new Map();
    points.forEach((point, index) => {
        const identifierAndKeyAndController = `${point.identifier}-${point.key}-${point.controller}`;
        if (
            identifiersAndKeyAndControllerMap.has(identifierAndKeyAndController)
        ) {
            const previousIndex = identifiersAndKeyAndControllerMap.get(
                identifierAndKeyAndController,
            );
            ctx.addIssue({
                code: "custom",
                path: [index],
            });
            ctx.addIssue({
                code: "custom",
                path: [previousIndex],
            });
        } else {
            identifiersAndKeyAndControllerMap.set(
                identifierAndKeyAndController,
                index,
            );
        }
    });
});

export const ApiStatusEnum = {
    Success: "Success",
    Error: "Error",
    Invalid: "Invalid",
    Pending: "Pending",
};

export default function PointBulkAddPopUp(props) {
    const {
        open,
        onCloseWithoutBulkImport,
        onCloseWithBulkImport,
        facility,
        controllers,
        floorPlans,
        tariffs,
    } = props;

    const { t } = useTranslation();

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [points, setPoints] = React.useState([]);
    const [pointApiStatusesByRowId, setPointApiStatusesByRowId] =
        React.useState({}); // [{ identifier, status: "Success" | "Error | Invalid", message? }]
    const [importValid, setImportValid] = React.useState(false);
    const [importInProgress, setImportInProgress] = React.useState(false);
    const [importComplete, setImportComplete] = React.useState(false);
    const [fileInputDisabled, setFileInputDisabled] = React.useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { pointsApi } = React.useContext(ApiContext);

    const translateSafeParseResult = React.useCallback(
        (safeParseResult) => {
            if (!safeParseResult || safeParseResult.success) {
                return safeParseResult;
            }

            const issues = safeParseResult?.error?.issues ?? [];
            return {
                success: false,
                error: {
                    ...safeParseResult.error,
                    issues: issues.map((issue) => ({
                        ...issue,
                        message: t(issue.message),
                    })),
                },
            };
        },
        [t],
    );

    const parseCsvContent = async (file) => {
        try {
            const data = await convertCsvFileToJson(file);
            const csvResultArray = data.map((row, index) => {
                const csvResultRaw = CsvPoint.safeParse(row);
                const csvResult = translateSafeParseResult(csvResultRaw);
                return csvResult;
            });
            let nextPoints = csvResultArray.map((csvResult, rowId) => ({
                csvResult,
                rowId,
            }));

            const arrayLevelParse = csvPointArray.safeParse(data);
            const rowDuplicationProblem = !arrayLevelParse.success;

            if (rowDuplicationProblem) {
                const duplicateRowIds = new Set(
                    arrayLevelParse.error.issues.map((issue) =>
                        Number(issue.path[0]),
                    ),
                );
                const affectedRowNumbers = [...duplicateRowIds].map(
                    (rowId) => rowId + 2,
                );
                nextPoints = csvResultArray.map((csvResult, rowId) => ({
                    csvResult,
                    rowId,
                    duplicate: duplicateRowIds.has(rowId),
                }));
                setAlertMessage(
                    t("Duplicate rows present in CSV file, rows affected: ") +
                        affectedRowNumbers.join(",") +
                        ".",
                );
                setAlertOpen(true);
                return nextPoints;
            } else {
                nextPoints = csvResultArray.map((csvResult, rowId) => {
                    if (!csvResult.success) {
                        return { csvResult, rowId };
                    }
                    const dtoResult = transformCsvPointToPointCreateDto(
                        csvResult.data,
                    );
                    return { dtoResult, csvResult, rowId };
                });
            }

            return nextPoints;
        } catch (error) {
            console.error("Error parsing CSV file:", error);
            setAlertMessage(
                t(
                    "Error parsing CSV file, please ensure it is properly formatted.",
                ),
            );
            setAlertOpen(true);
            return [];
        }
    };

    const areParseResultsAllValid = (points) => {
        return points.every(
            (point) => point.csvResult?.success && point.dtoResult?.success,
        );
    };

    const transformCsvPointToPointCreateDto = React.useCallback(
        (csvPoint) => {
            // Resolve a CSV name to its record URL, reporting names with no
            // matching record rather than silently dropping them
            const lookupIssues = [];
            const lookupUrl = (records, field, message) => {
                const name = csvPoint[field];
                if (!name) return undefined;
                const record = records.find((r) => r.name === name);
                if (!record) {
                    lookupIssues.push({ code: "custom", path: [field], message });
                }
                return record?.url;
            };

            const controllerUrl = lookupUrl(
                controllers,
                "controller",
                "Controller not found",
            );
            const tariffUrl = lookupUrl(tariffs, "tariff", "Tariff not found");
            const floorplanUrl = lookupUrl(
                floorPlans,
                "floorplan",
                "Floor plan not found",
            );

            const displayOnFloorPlanValue =
                csvPoint.display_on_floorplan ?? null;
            const displayOnFloorPlanBoolean =
                displayOnFloorPlanValue === "YES"
                    ? true
                    : displayOnFloorPlanValue === "NO"
                      ? false
                      : undefined;

            const dtoResultRaw = PointCreateDto.safeParse({
                facility: facility,
                floorplan: floorplanUrl,
                category: csvPoint.category,
                identifier: csvPoint.identifier,
                key: csvPoint.key,
                name: csvPoint.name,
                location: csvPoint.location,
                system: csvPoint.system,
                controller: controllerUrl,
                tariff: tariffUrl,
                display_on_floorplan: displayOnFloorPlanBoolean,
            });

            // Lookup issues go first so the grid shows "not found" rather
            // than the schema's generic "Field is required"
            if (lookupIssues.length > 0) {
                return translateSafeParseResult({
                    success: false,
                    error: {
                        issues: [
                            ...lookupIssues,
                            ...(dtoResultRaw.error?.issues ?? []),
                        ],
                    },
                });
            }
            return translateSafeParseResult(dtoResultRaw);
        },
        [controllers, tariffs, floorPlans, facility, translateSafeParseResult],
    );

    const makeSuccessHandler = (rowId) => {
        return (response) => {
            setPointApiStatusesByRowId((prev) => ({
                ...prev,
                [rowId]: { status: ApiStatusEnum.Success, url: response.url },
            }));
        };
    };

    const makeErrorHandler = (rowId) => {
        return (response) => {
            const non_field_errors = Object.entries(response)
                .filter(([key]) => key === "non_field_errors")
                .map(([key, value]) => value);
            if (non_field_errors.length > 0) {
                setAlertMessage(non_field_errors.join("; "));
                setAlertOpen(true);
            }
            setPointApiStatusesByRowId((prev) => ({
                ...prev,
                [rowId]: { status: ApiStatusEnum.Error, response },
            }));
        };
    };

    const submitPointsToApi = (pointsToSave) => {
        setImportInProgress(true);
        pointsToSave.forEach((point) => {
            if (point.dtoResult?.success) {
                const pointDto = point.dtoResult.data;

                const onSuccess = makeSuccessHandler(point.rowId);
                const onError = makeErrorHandler(point.rowId);

                setPointApiStatusesByRowId((prev) => ({
                    ...prev,
                    [point.rowId]: { status: ApiStatusEnum.Pending },
                }));
                pointsApi.create(pointDto, onSuccess, onError);
            }
        });
        setImportInProgress(false);
        setImportComplete(true);
    };

    const onFileSelect = async (file) => {
        setPoints([]);
        setPointApiStatusesByRowId({});
        setImportValid(false);
        const parsedPoints = await parseCsvContent(file);
        setPoints(parsedPoints);
        setImportValid(areParseResultsAllValid(parsedPoints));
        setImportComplete(false);
        setImportInProgress(false);
    };

    const handleImport = () => {
        submitPointsToApi(points);
    };

    const handleClose = () => {
        if (importInProgress) {
            return;
        }

        if (importComplete) {
            const successfulPointApiStatuses = Object.entries(
                pointApiStatusesByRowId,
            ).filter(([_, status]) => status.status === ApiStatusEnum.Success);

            const ImportedPointsWithUrls = successfulPointApiStatuses.map(
                ([rowId, status]) => {
                    const point = points.find((p) => p.rowId === Number(rowId));
                    if (point) {
                        return { ...point.dtoResult.data, url: status.url };
                    } else {
                        return null;
                    }
                },
            );

            onCloseWithBulkImport(ImportedPointsWithUrls);
        } else {
            onCloseWithoutBulkImport();
        }
        setPoints([]);
        setImportComplete(false);
        setFileInputDisabled(false);
    };

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
            >
                <DialogTitle>{t("Bulk import Points (csv)")}</DialogTitle>
                <DialogContent>
                    <PointCsvDataGrid
                        points={points}
                        pointApiStatusesByRowId={pointApiStatusesByRowId}
                        components={{ Toolbar: FileInputButton }}
                        componentsProps={{
                            toolbar: {
                                onFileSelect,
                                disabled: fileInputDisabled,
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        disabled={importInProgress}
                        autoFocus
                    >
                        {t("Close")}
                    </Button>
                    <Button
                        onClick={handleImport}
                        disabled={
                            importComplete || importInProgress || !importValid
                        }
                    >
                        {t("Import")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem saving data")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
